import React, { createContext, useContext, useState, useCallback } from 'react';
import { ToastContainer } from '../components/ui/Toast';
import type { ToastProps, ToastVariant, ToastAction } from '../components/ui/Toast';

const MAX_TOASTS = 5;

export interface ToastOptions {
  variant?: ToastVariant;
  duration?: number;
  action?: ToastAction;
}

interface ToastContextValue {
  toast: (message: string, options?: ToastOptions) => void;
  success: (message: string, options?: Omit<ToastOptions, 'variant'>) => void;
  error: (message: string, options?: Omit<ToastOptions, 'variant'>) => void;
  warning: (message: string, options?: Omit<ToastOptions, 'variant'>) => void;
  info: (message: string, options?: Omit<ToastOptions, 'variant'>) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

/**
 * ToastProvider - Manages toast notification state and rendering
 *
 * Usage:
 * ```tsx
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 * ```
 */
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  // Remove a toast by ID
  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Add a new toast with automatic ID generation
  const addToast = useCallback(
    (message: string, options?: ToastOptions) => {
      const id = crypto.randomUUID ? crypto.randomUUID() : `toast-${Date.now()}-${Math.random()}`;

      const newToast: ToastProps = {
        id,
        message,
        variant: options?.variant || 'info',
        duration: options?.duration || 5000,
        action: options?.action,
        onClose: removeToast,
      };

      setToasts((prev) => {
        // FIFO: Remove oldest toast if at max capacity
        if (prev.length >= MAX_TOASTS) {
          return [...prev.slice(1), newToast];
        }
        return [...prev, newToast];
      });
    },
    [removeToast]
  );

  // Convenience methods for specific variants
  const success = useCallback(
    (message: string, options?: Omit<ToastOptions, 'variant'>) => {
      addToast(message, { ...options, variant: 'success' });
    },
    [addToast]
  );

  const error = useCallback(
    (message: string, options?: Omit<ToastOptions, 'variant'>) => {
      addToast(message, { ...options, variant: 'error' });
    },
    [addToast]
  );

  const warning = useCallback(
    (message: string, options?: Omit<ToastOptions, 'variant'>) => {
      addToast(message, { ...options, variant: 'warning' });
    },
    [addToast]
  );

  const info = useCallback(
    (message: string, options?: Omit<ToastOptions, 'variant'>) => {
      addToast(message, { ...options, variant: 'info' });
    },
    [addToast]
  );

  return (
    <ToastContext.Provider
      value={{
        toast: addToast,
        success,
        error,
        warning,
        info,
      }}
    >
      {children}
      <ToastContainer toasts={toasts} />
    </ToastContext.Provider>
  );
};

/**
 * useToast hook - Access toast notification methods
 *
 * Usage:
 * ```tsx
 * const { success, error } = useToast();
 *
 * // Show success toast
 * success('Changes saved successfully');
 *
 * // Show error toast with action
 * error('Failed to save', {
 *   action: {
 *     label: 'Retry',
 *     onClick: handleRetry,
 *   },
 * });
 *
 * // Custom variant and duration
 * toast('Custom message', {
 *   variant: 'warning',
 *   duration: 3000,
 * });
 * ```
 */
export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within <ToastProvider />');
  }
  return ctx;
};
